import React, { useState, useEffect } from 'react';
import { historyAPI } from '../services/api';
import './History.css';

interface TimelineItem {
  year: string;
  title: string;
  description: string;
  significance: string;
}

interface KeyFigure {
  name: string;
  role: string;
  contribution: string;
}

interface Milestone {
  category: string;
  achievements: string[];
}

interface Impact {
  economic: string[];
  social: string[];
  environmental: string[];
}

const History: React.FC = () => {
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [figures, setFigures] = useState<KeyFigure[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [impact, setImpact] = useState<Impact | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchHistoryData();
  }, []);

  const fetchHistoryData = async () => {
    try {
      setLoading(true);
      const [timelineRes, figuresRes, milestonesRes, impactRes] = await Promise.all([
        historyAPI.getTimeline(),
        historyAPI.getFigures(),
        historyAPI.getMilestones(),
        historyAPI.getImpact()
      ]);

      setTimeline(timelineRes.data.data);
      setFigures(figuresRes.data.data);
      setMilestones(milestonesRes.data.data);
      setImpact(impactRes.data.data);
    } catch (err) {
      setError('Error loading history data. Please try again later.');
      console.error('Error fetching history:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="history-page">
        <div className="container">
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading history...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="history-page">
        <div className="container">
          <div className="error">
            <h2>Error Loading History</h2>
            <p>{error}</p>
            <button onClick={fetchHistoryData} className="btn btn-primary">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="history-page">
      <div className="container">
        {/* Header */}
        <div className="history-header">
          <h1>History of Oil & Gas in Guyana</h1>
          <p>Explore the remarkable journey of Guyana's oil and gas sector from discovery to production</p>
        </div>

        {/* Timeline Section */}
        <section className="timeline-section">
          <h2>Key Milestones Timeline</h2>
          <div className="timeline">
            {timeline.map((item, index) => (
              <div key={index} className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}>
                <div className="timeline-content">
                  <div className="timeline-year">{item.year}</div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  <div className="significance">
                    <strong>Significance:</strong> {item.significance}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Key Figures Section */}
        <section className="figures-section">
          <h2>Key Figures</h2>
          <div className="figures-grid">
            {figures.map((figure, index) => (
              <div key={index} className="figure-card">
                <h3>{figure.name}</h3>
                <p className="role">{figure.role}</p>
                <p className="contribution">{figure.contribution}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Milestones Section */}
        <section className="milestones-section">
          <h2>Major Achievements</h2>
          <div className="milestones-grid">
            {milestones.map((milestone, index) => (
              <div key={index} className="milestone-card">
                <h3>{milestone.category}</h3>
                <ul>
                  {milestone.achievements.map((achievement, idx) => (
                    <li key={idx}>{achievement}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Impact Section */}
        {impact && (
          <section className="impact-section">
            <h2>Industry Impact</h2>
            <div className="impact-grid">
              <div className="impact-card">
                <h3>Economic Impact</h3>
                <ul>
                  {impact.economic.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
              <div className="impact-card">
                <h3>Social Impact</h3>
                <ul>
                  {impact.social.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
              <div className="impact-card">
                <h3>Environmental Impact</h3>
                <ul>
                  {impact.environmental.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default History;
